import { useState } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCleanupSampleData, useSetCleanupChoice } from "@/hooks/useSampleData";
import { Trash2, Archive, BellOff, Loader2 } from "lucide-react";

interface MockCleanupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Three-choice dialog shown the first time a user connects a non-sample
 * (demo/real) broker account while sample data is still present.
 *
 * Choices:
 *  - delete     → remove all sample data immediately
 *  - keep       → keep sample data as a separate read-only account
 *  - never_ask  → keep, and never prompt again
 */
export function MockCleanupDialog({ open, onOpenChange }: MockCleanupDialogProps) {
  const { language } = useLanguage();
  const fa = language === "fa";
  const cleanup = useCleanupSampleData();
  const setChoice = useSetCleanupChoice();
  const [pending, setPending] = useState<"delete" | "keep" | "never_ask" | null>(null);

  const handle = async (choice: "delete" | "keep" | "never_ask") => {
    setPending(choice);
    try {
      if (choice === "delete") {
        await cleanup.mutateAsync();
      }
      await setChoice.mutateAsync(choice);
      onOpenChange(false);
    } finally {
      setPending(null);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-base">
            {fa
              ? "🎉 اولین اکانت بروکر شما متصل شد!"
              : "🎉 Your first broker account is connected!"}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-sm">
            {fa
              ? "می‌خواهید با داده‌های نمونه (Sample) که برای آشنایی ساخته شده بود چه کنیم؟"
              : "What should we do with the sample data we created for you to explore the platform?"}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2 py-2">
          <button
            type="button"
            disabled={!!pending}
            onClick={() => handle("delete")}
            className="w-full text-left rounded-md border border-border hover:border-destructive/40 hover:bg-destructive/5 p-3 transition-colors disabled:opacity-50"
          >
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-md bg-destructive/10 text-destructive p-1.5">
                {pending === "delete" ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">
                  {fa ? "حذف کن" : "Delete"}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {fa
                    ? "همه‌ی داده‌های نمونه (اکانت، معاملات، پلن‌ها) برای همیشه پاک شوند."
                    : "Permanently remove sample account, trades, plans and behavior data."}
                </p>
              </div>
            </div>
          </button>

          <button
            type="button"
            disabled={!!pending}
            onClick={() => handle("keep")}
            className="w-full text-left rounded-md border border-border hover:border-primary/40 hover:bg-primary/5 p-3 transition-colors disabled:opacity-50"
          >
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-md bg-primary/10 text-primary p-1.5">
                {pending === "keep" ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Archive className="w-4 h-4" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">
                  {fa ? "نگه‌دار به‌عنوان اکانت Sample جدا" : "Keep as a separate Sample account"}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {fa
                    ? "داده‌ها در یک اکانت جدا (read-only) باقی می‌مانند و هرگز با اکانت واقعی شما قاطی نمی‌شوند."
                    : "Sample data stays in a separate read-only account and never mixes with real data."}
                </p>
              </div>
            </div>
          </button>

          <button
            type="button"
            disabled={!!pending}
            onClick={() => handle("never_ask")}
            className="w-full text-left rounded-md border border-border hover:border-muted-foreground/40 hover:bg-muted p-3 transition-colors disabled:opacity-50"
          >
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-md bg-muted text-muted-foreground p-1.5">
                {pending === "never_ask" ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <BellOff className="w-4 h-4" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">
                  {fa ? "هیچ‌وقت دیگه نپرس" : "Don't ask me again"}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {fa
                    ? "نگه‌دار، ولی این پنجره دیگر نمایش داده نشود. هر وقت خواستی از Settings پاکش کن."
                    : "Keep the data but don't show this dialog again. You can clean up later from Settings."}
                </p>
              </div>
            </div>
          </button>
        </div>

        <AlertDialogFooter>
          <Button
            variant="ghost"
            size="sm"
            disabled={!!pending}
            onClick={() => onOpenChange(false)}
          >
            {fa ? "بعداً تصمیم می‌گیرم" : "Decide later"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
